import {Singleton} from 'annotation';
import environment from 'environment';

import ProxyService from './Proxy';
import Parameter from './Parameter';

@Singleton
export default class CashierService extends ProxyService {

    public api: string = environment.CASHIER_API || environment.BASE_API + 'v1/merchants/cashier';

    // Categories
    public findCategories(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/categories`, param);
        return this.request(parameter);
    }

    public saveCategory(param: any): Promise<any> {
        const parameter = new Parameter(param)
            .setMethod(Parameter.methods.POST)
            .setUrl(`${this.api}/categories`);
        return this.request(parameter);
    }

    public updateCategory(id: number, param: any): Promise<any> {
        const parameter = new Parameter(param)
            .setMethod(Parameter.methods.PUT)
            .setUrl(`${this.api}/categories/${id}`);
        return this.request(parameter);
    }

    public deleteCategory(id: number): Promise<any> {
        const parameter = new Parameter()
            .setMethod(Parameter.methods.DELETE)
            .setUrl(`${this.api}/categories/${id}`);
        return this.request(parameter);
    }

    // Products
    public findProducts(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/products`, param);
        return this.request(parameter);
    }

    public findProductById(id: number): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/products/${id}`);
        return this.request(parameter);
    }

    public saveProduct(param: any): Promise<any> {
        const parameter = new Parameter(param)
            .setMethod(Parameter.methods.POST)
            .setUrl(`${this.api}/products`);
        return this.request(parameter);
    }

    public updateProduct(id: number, param: any): Promise<any> {
        const parameter = new Parameter(param)
            .setMethod(Parameter.methods.PUT)
            .setUrl(`${this.api}/products/${id}`);
        return this.request(parameter);
    }

    public deleteProduct(id: number): Promise<any> {
        const parameter = new Parameter()
            .setMethod(Parameter.methods.DELETE)
            .setUrl(`${this.api}/products/${id}`);
        return this.request(parameter);
    }

    public downloadProductTemplate(): Promise<any> {
        const fileName = 'Template_Produk.xlsx';
        return super.downloadFile(`${this.api}/products/template`, fileName, false);
    }

    public uploadProduct(file: File, param?: any): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        if (param) {
            formData.append('payload', JSON.stringify(param));
        }

        const parameter = new Parameter(formData)
            .setMethod(Parameter.methods.POST)
            .setUrl(`${this.api}/products/upload`)
            .setEnableNotif(false);

        return this.request(parameter);
    }

    // Stock Operations
    public findProductStock(id: number): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/products/${id}/stock`);
        return this.request(parameter);
    }

    public stockIn(id: number, param: any): Promise<any> {
        const parameter = new Parameter(param)
            .setMethod(Parameter.methods.POST)
            .setUrl(`${this.api}/products/${id}/stock/in`)
            .setEnableNotif(false);
        return this.request(parameter);
    }

    public stockOut(id: number, param: any): Promise<any> {
        const parameter = new Parameter(param)
            .setMethod(Parameter.methods.POST)
            .setUrl(`${this.api}/products/${id}/stock/out`)
            .setEnableNotif(false);
        return this.request(parameter);
    }

    public findStockHistory(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/stock/history`, param);
        return this.request(parameter);
    }

    public findStockHistoryIn(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/stock/history/in`, param);
        return this.request(parameter);
    }

    public findStockHistoryOut(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/stock/history/out`, param);
        return this.request(parameter);
    }

    // Transactions
    public findTransactions(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/transactions`, param);
        return this.request(parameter);
    }

    public findTransactionById(id: number): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/transactions/${id}`);
        return this.request(parameter);
    }

    public cancelTransaction(id: number, param?: any): Promise<any> {
        const parameter = new Parameter(param || {})
            .setMethod(Parameter.methods.POST)
            .setUrl(`${this.api}/transactions/${id}/cancel`);
        return this.request(parameter);
    }

    // Dashboard - KANTIN
    public findDashboardSummary(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/dashboard/summary`, param);
        return this.request(parameter);
    }

    public findDashboardOmzetTrend(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/dashboard/omzet-trend`, param);
        return this.request(parameter);
    }

    public findDashboardTopProducts(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/dashboard/top-products`, param);
        return this.request(parameter);
    }

    public findDashboardMargin(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/dashboard/margin`, param);
        return this.request(parameter);
    }

    // Dashboard - PETUGAS_KANTIN
    public findDashboardKasirSummary(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/dashboard-kasir/summary`, param);
        return this.request(parameter);
    }

    public findDashboardKasirRecentTransactions(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/dashboard-kasir/recent-transactions`, param);
        return this.request(parameter);
    }

    // Reports - Sales
    public findSalesReport(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/sales`, param);
        return this.request(parameter);
    }

    public findSalesReportByProducts(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/sales/products`, param);
        return this.request(parameter);
    }

    public findSalesReportByCategories(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/sales/categories`, param);
        return this.request(parameter);
    }

    public exportSalesReport(param: any): Promise<any> {
        const fileName = `Laporan_Penjualan_${param.start_date}_${param.end_date}.xlsx`;
        const queryString = new URLSearchParams(param).toString();
        const url = `${this.api}/reports/sales/export?${queryString}`;
        return super.downloadFile(url, fileName, false);
    }

    // Reports - Finance
    public findFinanceOmzetTrend(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/finance/omzet-trend`, param);
        return this.request(parameter);
    }

    public findFinanceMargin(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/finance/margin`, param);
        return this.request(parameter);
    }

    public findFinanceCashflow(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/finance/cashflow`, param);
        return this.request(parameter);
    }

    public findFinanceProfitLoss(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/finance/profit-loss`, param);
        return this.request(parameter);
    }

    public exportFinanceReport(param: any): Promise<any> {
        const fileName = `Laporan_Keuangan_${param.start_date}_${param.end_date}.xlsx`;
        const queryString = new URLSearchParams(param).toString();
        const url = `${this.api}/reports/finance/export?${queryString}`;
        return super.downloadFile(url, fileName, false);
    }

    // Reports - Stock
    public findStockReportCurrent(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/stock/current`, param);
        return this.request(parameter);
    }

    public findStockReportMovements(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/stock/movements`, param);
        return this.request(parameter);
    }

    public findStockReportLowStock(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/stock/low-stock`, param);
        return this.request(parameter);
    }

    public findStockReportAudit(param: any): Promise<any> {
        const parameter = new Parameter().setUrl(`${this.api}/reports/stock/audit`, param);
        return this.request(parameter);
    }

    public exportStockReport(param: any): Promise<any> {
        const fileName = `Laporan_Stok_${param.start_date}_${param.end_date}.xlsx`;
        const queryString = new URLSearchParams(param).toString();
        const url = `${this.api}/reports/stock/export?${queryString}`;
        return super.downloadFile(url, fileName, false);
    }
}

